import React, { useState, useEffect, useContext } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Alert,
  Spinner,
  Badge,
  Table,
  Form,
  Modal,
  ButtonGroup,
  Carousel
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCar, faWrench, faPlus, faTrash, faBarcode, faEdit, faCamera, faUpload, faSync, faImage, faExclamationTriangle, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { carModelBodyTypeLabel } from 'components/vehicles/CarModelBodyTypeSelect';
import { useBranchContext } from 'providers/BranchProvider';
import SearchBox from 'components/common/SearchBox';
import { UnitOfMeasure } from 'utils/enums';
import { RetryImage } from 'components/common/RetryImage';
import { ImageRotateControls, useImageRotation } from 'components/modals/ImageRotateControls';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import ComplaintEditor, { extractComplaintPlainText, RichHtmlContent } from 'components/editors/ComplaintEditor';
import { AuthContext } from 'providers/AuthProvider';
import { hasAnyRole } from 'utils/roleAccess';
import HtmlPdfPreviewModal from 'components/modals/HtmlPdfPreviewModal';
import {
  DocumentPreview,
  parseDocumentPreviewResponse,
  revokeDocumentPreview
} from 'utils/printHtmlAsPdf';

/** Matches API VehicleImageType.Vehicle */
const VEHICLE_IMAGE_TYPE_VEHICLE = 1;

// Types
type VehicleServiceStatus = 'New' | 'InProgress' | 'Submitted';
type VehicleServicePaymentStatus = 'Unpaid' | 'Paid';
type VehicleServicePaymentMethod = 'Cash' | 'ElectronicTransfer' | 'Card' | 'Check';

interface VehicleImageDto {
  vehicleImageId: number;
  googleFileId: string;
  vehicleImageCategoryId?: number;
  vehicleImageCategoryName?: string;
  /** API may send numeric enum or string name (Newtonsoft StringEnumConverter). */
  vehicleImageType?: number | string;
}

const isVehicleTaggedImage = (img: VehicleImageDto): boolean => {
  const type = img.vehicleImageType;
  return (
    type === VEHICLE_IMAGE_TYPE_VEHICLE
    || type === 'Vehicle'
    || img.vehicleImageCategoryName === 'Vehicle'
  );
};

interface VehicleServiceDetailDto {
  vehicleServiceId: number;
  vehicleId: number;
  currentMileage: number;
  createdOn: string;
  customerRequestComplaint: string;
  customerRequestComplaintPlainText?: string;
  serviceTitle?: string;
  serviceResolution?: string;
  status: VehicleServiceStatus;
  afterServiceDetails?: string;
  paymentStatus?: VehicleServicePaymentStatus;
  paymentMethod?: VehicleServicePaymentMethod;
  paymentMethodDisplay?: string;
  hasPaymentProof?: boolean;
  hasServiceSummary?: boolean;
  mileageMissing?: boolean;
  actingLeadMechanicUserId?: number | null;
  actingLeadMechanicName?: string | null;
  referrer?: string | null;
  droppedOffOn?: string | null;
  vehicle: {
    vehicleId: string;
    plateNumber: string;
    conductionNumber: string;
    chassisNo?: string;
    carMake: string;
    carModel: string;
    bodyType?: string;
    year: number;
    customerName: string;
    color?: string;
    fuelType?: string;
    transmission?: string;
  };
}

interface VehicleServiceItemImageDto {
  vehicleImageId: number;
  googleFileId: string;
  contentType: string;
  filename: string;
  vehicleImageCategoryId?: number;
  vehicleImageCategoryName?: string;
  vehicleImageType?: number | string;
  categoryAnalysisStatus?: number | string;
  isCategoryAnalysisPending?: boolean;
}

interface VehicleServiceItemDto {
  vehicleServiceItemId: number;
  vehicleServiceId: number;
  quantity: number;
  unitOfMeasure: UnitOfMeasure;
  productId?: number;
  productName?: string;
  isAService?: boolean;
  imagesNotRequired?: boolean;
  imagesRequired?: boolean;
  missingRequiredImageTypes?: string[];
  procurementInvoiceId?: number;
  noIdProductName?: string;
  shopServiceId?: number | null;
  priceOverrideReason?: string | null;
  mechanics?: { userId: number; name: string }[];
  startedOn?: string | null;
  endedOn?: string | null;
  unitPrice: number;
  amount: number;
  procurementPrice?: number;
  isDeleted?: boolean;
  vehicleServiceItemImages?: VehicleServiceItemImageDto[];
}

const haveItemImagesChanged = (
  prevItems: VehicleServiceItemDto[],
  nextItems: VehicleServiceItemDto[]
) => {
  if (prevItems.length !== nextItems.length) return true;

  for (let i = 0; i < nextItems.length; i++) {
    const prev = prevItems[i];
    const next = nextItems[i];
    if (prev.vehicleServiceItemId !== next.vehicleServiceItemId) return true;

    const prevImages = prev.vehicleServiceItemImages ?? [];
    const nextImages = next.vehicleServiceItemImages ?? [];
    if (prevImages.length !== nextImages.length) return true;

    for (let j = 0; j < nextImages.length; j++) {
      const p = prevImages[j];
      const n = nextImages[j];
      if (
        p.vehicleImageId !== n.vehicleImageId
        || p.isCategoryAnalysisPending !== n.isCategoryAnalysisPending
        || p.vehicleImageCategoryId !== n.vehicleImageCategoryId
        || p.vehicleImageCategoryName !== n.vehicleImageCategoryName
        || p.googleFileId !== n.googleFileId
      ) {
        return true;
      }
    }

    const prevMissing = (prev.missingRequiredImageTypes ?? []).join('|');
    const nextMissing = (next.missingRequiredImageTypes ?? []).join('|');
    if (prevMissing !== nextMissing) return true;
  }

  return false;
};

interface CreateVehicleServiceItemRequest {
  quantity: number;
  unitOfMeasure: UnitOfMeasure;
  productId?: number;
  procurementInvoiceId?: number;
  noIdProductName?: string;
  shopServiceId?: number;
  priceOverrideReason?: string;
  unitPrice: number;
}

interface ServiceQuote {
  shopServiceId: number;
  code: string;
  name: string;
  categoryName: string;
  description?: string | null;
  minPrice?: number | null;
  standardPrice?: number | null;
  maxPrice?: number | null;
  isCarModelPrice: boolean;
}

interface BranchMechanic {
  userId: number;
  name: string;
}

const clockInputValue = (value?: string | null) => (value ? value.slice(0, 16) : '');

const nowClockParts = () => {
  const now = new Date();
  const pad = (part: number) => String(part).padStart(2, '0');
  return {
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`
  };
};

const todayDateInput = () => nowClockParts().date;

const splitServiceClock = (value?: string | null) => {
  const input = clockInputValue(value);
  if (!input) return nowClockParts();
  return { date: input.slice(0, 10), time: input.slice(11, 16) };
};

const joinServiceClock = (date: string, time: string) => (date && time ? `${date}T${time}` : '');

const formatServiceClock = (value?: string | null) => {
  const input = clockInputValue(value);
  if (!input) return null;
  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) return input.replace('T', ' ');
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const apiErrorMessage = (err: unknown, fallback: string) => {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { errorMessage?: string } | undefined;
    if (data?.errorMessage) return data.errorMessage;
  }
  return fallback;
};

interface ServiceQuoteResult {
  bodyType?: string | null;
  priceTier?: string | null;
  carModelName?: string | null;
  services: ServiceQuote[];
}

interface BarcodeProduct {
  productId: number;
  productName: string;
  procurementInvoiceId: number;
  sellingPrice: number;
  procurementPrice: number;
}

const unitOptions = [
  { value: UnitOfMeasure.Piece, label: 'Piece' },
  { value: UnitOfMeasure.Set, label: 'Set' },
  { value: UnitOfMeasure.Meter, label: 'Meter' },
  { value: UnitOfMeasure.Foot, label: 'Foot' },
  { value: UnitOfMeasure.Inches, label: 'Inches' }
];

const VehicleServiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { vehicleServiceId } = useParams<{ vehicleServiceId: string }>();
  const { selectedBranch } = useBranchContext();
  const { token } = useContext(AuthContext) || { token: null };
  const canManageAccounting = hasAnyRole(token?.roles, [
    'Admin',
    'AccountingStaff',
    'AccountingLead'
  ]);
  const canEditService = hasAnyRole(token?.roles, ['Admin', 'ServiceAdvisor', 'Mechanic']);
  
  // State
  const [vehicleService, setVehicleService] = useState<VehicleServiceDetailDto | null>(null);
  const [items, setItems] = useState<VehicleServiceItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  
  // Add item state
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [itemFormData, setItemFormData] = useState<CreateVehicleServiceItemRequest>({
    quantity: 1,
    unitOfMeasure: UnitOfMeasure.Piece,
    productId: undefined,
    procurementInvoiceId: undefined,
    noIdProductName: '',
    unitPrice: 0
  });
  const [addingItem, setAddingItem] = useState(false);
  const [barcodeProduct, setBarcodeProduct] = useState<BarcodeProduct | null>(null);
  const [fromBarcodeDetails, setFromBarcodeDetails] = useState(false);
  const [itemMode, setItemMode] = useState<'part' | 'service'>('part');
  const [mechanics, setMechanics] = useState<BranchMechanic[]>([]);
  const [savingLead, setSavingLead] = useState(false);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [editLeadId, setEditLeadId] = useState<number | ''>('');
  const [savingReferrer, setSavingReferrer] = useState(false);
  const [showEditReferrerModal, setShowEditReferrerModal] = useState(false);
  const [editReferrer, setEditReferrer] = useState('');
  const [savingDropOff, setSavingDropOff] = useState(false);
  const [showEditDropOffModal, setShowEditDropOffModal] = useState(false);
  const [editDropOffDate, setEditDropOffDate] = useState(todayDateInput);
  const [editDropOffTime, setEditDropOffTime] = useState('');
  const [savingLabor, setSavingLabor] = useState(false);
  const [laborEditItem, setLaborEditItem] = useState<VehicleServiceItemDto | null>(null);
  const [editLaborMechanicIds, setEditLaborMechanicIds] = useState<number[]>([]);
  const [editLaborStartDate, setEditLaborStartDate] = useState(todayDateInput);
  const [editLaborStartTime, setEditLaborStartTime] = useState('');
  const [editLaborEndDate, setEditLaborEndDate] = useState(todayDateInput);
  const [editLaborEndTime, setEditLaborEndTime] = useState('');
  const [serviceQuotes, setServiceQuotes] = useState<ServiceQuoteResult | null>(null);
  const [serviceQuery, setServiceQuery] = useState('');
  const [selectedService, setSelectedService] = useState<ServiceQuote | null>(null);
  
  // Submit service state
  const [submittingService, setSubmittingService] = useState(false);
  const [showSubmitWarningModal, setShowSubmitWarningModal] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [showMileageConfirmModal, setShowMileageConfirmModal] = useState(false);
  const [showRemoveItemModal, setShowRemoveItemModal] = useState(false);
  const [itemIdToRemove, setItemIdToRemove] = useState<number | null>(null);
  const [removingItem, setRemovingItem] = useState(false);

  // Image upload state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedItemForImages, setSelectedItemForImages] = useState<VehicleServiceItemDto | null>(null);
  const [cameraMode, setCameraMode] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraId, setCurrentCameraId] = useState<string>('');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  // Image zoom / carousel state
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [carouselImages, setCarouselImages] = useState<VehicleServiceItemImageDto[]>([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const zoomRotation = useImageRotation(
    showZoomModal ? carouselImages[carouselIndex]?.googleFileId : undefined
  );
  
  // Vehicle image categories state
  const [vehicleImageCategories, setVehicleImageCategories] = useState<Array<{vehicleImageCategoryId: number, vehicleImageCategoryName: string}>>([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedImageForCategory, setSelectedImageForCategory] = useState<{vehicleImageId: number, filename: string, currentCategoryId?: number} | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [updatingCategory, setUpdatingCategory] = useState(false);

  // Edit service fields state - using modals
  const [showEditDescriptionModal, setShowEditDescriptionModal] = useState(false);
  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [showEditResolutionModal, setShowEditResolutionModal] = useState(false);
  const [showEditAfterServiceDetailsModal, setShowEditAfterServiceDetailsModal] = useState(false);
  const [showEditMileageModal, setShowEditMileageModal] = useState(false);
  const [editComplaint, setEditComplaint] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editResolution, setEditResolution] = useState('');
  const [editAfterServiceDetails, setEditAfterServiceDetails] = useState('');
  const [editMileage, setEditMileage] = useState(0);
  const [updatingService, setUpdatingService] = useState(false);
  const [paymentProofFile, setPaymentProofFile] = useState<File | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<VehicleServicePaymentMethod | ''>('');
  const [uploadingPaymentProof, setUploadingPaymentProof] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState<DocumentPreview | null>(null);
  const [vehicleProfileImageId, setVehicleProfileImageId] = useState<string | null>(null);
  const [vehicleProfileImages, setVehicleProfileImages] = useState<VehicleServiceItemImageDto[]>([]);

  // Helper function to get status display
  const getStatusDisplay = (status: VehicleServiceStatus) => {
    switch (status) {
      case 'New':
        return { text: 'New', color: 'warning' };
      case 'InProgress':
        return { text: 'In Progress', color: 'info' };
      case 'Submitted':
        return { text: 'Submitted', color: 'success' };
      default:
        return { text: 'Unknown', color: 'secondary' };
    }
  };

  // Fetch vehicle service details
  const fetchVehicleServiceDetails = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) {
      setError('Vehicle service ID or branch domain not provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get(`/api/v1/${selectedBranch.branchDomain}/vehicles/0/services/details/${vehicleServiceId}`);
      setVehicleService(response.data);
      await Promise.all([
        fetchVehicleServiceItems(),
        fetchVehicleProfileImage(response.data.vehicleId as number)
      ]);
    } catch (err) {
      console.error('Error fetching vehicle service details:', err);
      setError('Failed to load vehicle service details');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleProfileImage = async (vehicleId: number) => {
    if (!vehicleId) {
      setVehicleProfileImageId(null);
      setVehicleProfileImages([]);
      return;
    }

    try {
      const response = await axiosInstance.get(`/api/v1/vehicles/${vehicleId}`);
      const images = (response.data?.vehicleImages || []) as VehicleImageDto[];
      const vehiclePhotos = images.filter(isVehicleTaggedImage);
      const zoomImages: VehicleServiceItemImageDto[] = vehiclePhotos.map((img, index) => ({
        vehicleImageId: img.vehicleImageId,
        googleFileId: img.googleFileId,
        contentType: 'image/jpeg',
        filename: `Vehicle photo ${index + 1}`,
        vehicleImageCategoryId: img.vehicleImageCategoryId,
        vehicleImageCategoryName: img.vehicleImageCategoryName,
        vehicleImageType: img.vehicleImageType
      }));
      setVehicleProfileImages(zoomImages);
      setVehicleProfileImageId(zoomImages[0]?.googleFileId || null);
    } catch (err) {
      console.error('Error fetching vehicle profile image:', err);
      setVehicleProfileImageId(null);
      setVehicleProfileImages([]);
    }
  };

  // Fetch vehicle service items
  const fetchVehicleServiceItems = async (options?: { silent?: boolean }): Promise<VehicleServiceItemDto[] | undefined> => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return undefined;

    try {
      if (!options?.silent) {
        setItemsLoading(true);
      }
      const response = await axiosInstance.get(`/api/v1/${selectedBranch.branchDomain}/vehicle-services/${vehicleServiceId}/items`);
      const nextItems = response.data as VehicleServiceItemDto[];

      setItems(prev => {
        if (options?.silent && !haveItemImagesChanged(prev, nextItems)) {
          return prev;
        }
        return nextItems;
      });
      setSelectedItemForImages(prev => {
        if (!prev) return prev;
        const updated = nextItems.find(i => i.vehicleServiceItemId === prev.vehicleServiceItemId);
        if (!updated) return prev;
        if (options?.silent && !haveItemImagesChanged([prev], [updated])) {
          return prev;
        }
        return updated;
      });
      setCarouselImages(prev => {
        if (!prev.length) return prev;
        const byId = new Map(
          nextItems.flatMap(item => item.vehicleServiceItemImages ?? [])
            .map(img => [img.vehicleImageId, img] as const)
        );
        const next = prev.map(img => byId.get(img.vehicleImageId) ?? img);
        const changed = next.some((img, i) =>
          img.vehicleImageCategoryId !== prev[i].vehicleImageCategoryId
          || img.isCategoryAnalysisPending !== prev[i].isCategoryAnalysisPending
          || img.vehicleImageCategoryName !== prev[i].vehicleImageCategoryName
        );
        return changed ? next : prev;
      });
      return nextItems;
    } catch (err) {
      console.error('Error fetching vehicle service items:', err);
      return undefined;
    } finally {
      if (!options?.silent) {
        setItemsLoading(false);
      }
    }
  };
  
  // Fetch vehicle image categories
  const fetchVehicleImageCategories = async () => {
    try {
      const response = await axiosInstance.get('/api/v1/vehicleImageCategories');
      setVehicleImageCategories(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching vehicle image categories:', err);
    }
  };

  useEffect(() => {
    if (!showAddItemModal || !vehicleServiceId) return;
    let cancelled = false;
    axiosInstance
      .get<ServiceQuoteResult>('/api/v1/shop-services/quotes', {
        params: { vehicleServiceId }
      })
      .then(response => {
        if (!cancelled) setServiceQuotes(response.data);
      })
      .catch(() => {
        if (!cancelled) setServiceQuotes(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showAddItemModal, vehicleServiceId]);

  const chooseService = (service: ServiceQuote) => {
    setSelectedService(service);
    setItemFormData(current => ({
      ...current,
      productId: undefined,
      procurementInvoiceId: undefined,
      noIdProductName: service.name,
      shopServiceId: service.shopServiceId,
      unitPrice: service.standardPrice ?? 0,
      priceOverrideReason: undefined
    }));
  };

  const serviceOutsideBand = selectedService?.minPrice != null
    && selectedService.maxPrice != null
    && (itemFormData.unitPrice < selectedService.minPrice || itemFormData.unitPrice > selectedService.maxPrice);

  const filteredServices = (serviceQuotes?.services ?? []).filter(service => {
    const needle = serviceQuery.trim().toLowerCase();
    if (!needle) return true;
    return service.name.toLowerCase().includes(needle)
      || service.code.toLowerCase().includes(needle)
      || service.categoryName.toLowerCase().includes(needle);
  });

  // Handle barcode scanning
  const handleBarcodeChange = async (barcode: string) => {
    if (!barcode) {
      setBarcodeProduct(null);
      setFromBarcodeDetails(false);
      return;
    }

    try {
      setFromBarcodeDetails(true);
      const response = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/products/byBarcode?barcode=${barcode}`
      );
      
      const product = response.data;
      setBarcodeProduct(product);
      setItemFormData({
        ...itemFormData,
        productId: product.productId,
        procurementInvoiceId: product.procurementInvoiceId,
        unitPrice: product.sellingPrice,
        noIdProductName: '',
        shopServiceId: undefined,
        priceOverrideReason: undefined
      });
      setSelectedService(null);
    } catch (err) {
      console.error('Error fetching product by barcode:', err);
      setBarcodeProduct(null);
      setFromBarcodeDetails(false);
    }
  };

  // Handle add item
  const handleAddItem = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;

    try {
      setAddingItem(true);
      
      const outsideBand = selectedService?.minPrice != null
        && selectedService.maxPrice != null
        && (itemFormData.unitPrice < selectedService.minPrice || itemFormData.unitPrice > selectedService.maxPrice);
      if (itemMode === 'service' && outsideBand && !itemFormData.priceOverrideReason?.trim()) {
        toast.error('Enter a reason for charging outside the allowed price.', toastConfig);
        return;
      }

      await axiosInstance.post(`/api/v1/${selectedBranch.branchDomain}/vehicle-services/${vehicleServiceId}/items`, {
        ...itemFormData,
        shopServiceId: itemMode === 'service' ? selectedService?.shopServiceId : undefined,
        priceOverrideReason: itemMode === 'service' && outsideBand
          ? itemFormData.priceOverrideReason?.trim()
          : undefined
      });
      
      // Reset form and close modal
      setItemFormData({
        quantity: 1,
        unitOfMeasure: UnitOfMeasure.Piece,
        productId: undefined,
        procurementInvoiceId: undefined,
        noIdProductName: '',
        shopServiceId: undefined,
        priceOverrideReason: undefined,
        unitPrice: 0
      });
      setBarcodeInput('');
      setBarcodeProduct(null);
      setFromBarcodeDetails(false);
      setSelectedService(null);
      setServiceQuery('');
      setItemMode('part');
      setShowAddItemModal(false);
      
      // Refresh items
      await fetchVehicleServiceItems();
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setAddingItem(false);
    }
  };

  // Handle delete item
  const openRemoveItemModal = (itemId: number) => {
    setItemIdToRemove(itemId);
    setShowRemoveItemModal(true);
  };

  const closeRemoveItemModal = () => {
    if (removingItem) return;
    setShowRemoveItemModal(false);
    setItemIdToRemove(null);
  };

  const confirmRemoveItem = async () => {
    if (!itemIdToRemove || !selectedBranch?.branchDomain) return;

    try {
      setRemovingItem(true);
      await axiosInstance.delete(`/api/v1/${selectedBranch.branchDomain}/vehicle-services/${vehicleServiceId}/items/${itemIdToRemove}`);
      toast.success('Service item removed', toastConfig);
      setShowRemoveItemModal(false);
      setItemIdToRemove(null);
      await fetchVehicleServiceItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.errorMessage || 'Failed to remove service item', toastConfig);
      } else {
        toast.error('Failed to remove service item', toastConfig);
      }
    } finally {
      setRemovingItem(false);
    }
  };

  // Handle submit service
  const itemsMissingRequiredImages = items.filter(
    item => !item.isDeleted && item.imagesRequired && (item.missingRequiredImageTypes?.length ?? 0) > 0
  );

  const submitService = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;

    try {
      setSubmittingService(true);
      setShowSubmitWarningModal(false);
      setShowSubmitConfirmModal(false);
      setShowMileageConfirmModal(false);

      await axiosInstance.post(`/api/v1/${selectedBranch.branchDomain}/vehicles/0/services/${vehicleServiceId}/submit`);
      toast.success('Service submitted successfully', toastConfig);
      await fetchVehicleServiceDetails();
    } catch (err) {
      console.error('Error submitting service:', err);
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.errorMessage || 'Failed to submit service', toastConfig);
      } else {
        toast.error('Failed to submit service', toastConfig);
      }
    } finally {
      setSubmittingService(false);
    }
  };

  const continueSubmitAfterMileageCheck = () => {
    setShowMileageConfirmModal(false);
    if (itemsMissingRequiredImages.length > 0) {
      setShowSubmitWarningModal(true);
      return;
    }
    setShowSubmitConfirmModal(true);
  };

  const handleSubmitService = () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;

    if ((vehicleService?.currentMileage ?? 0) <= 0) {
      setShowMileageConfirmModal(true);
      return;
    }

    if (itemsMissingRequiredImages.length > 0) {
      setShowSubmitWarningModal(true);
      return;
    }

    setShowSubmitConfirmModal(true);
  };

  const handleViewSummary = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain || !vehicleService) return;
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleServiceId}/summary`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Service summary is not available yet', toastConfig);
    }
  };

  const closeInvoicePreview = () => {
    setShowInvoicePreview(false);
    setInvoicePreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
  };

  const handleDownloadInvoice = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain || !vehicleService) return;
    setInvoicePreview(prev => {
      revokeDocumentPreview(prev);
      return null;
    });
    setShowInvoicePreview(true);
    setDownloadingInvoice(true);
    try {
      const response = await axiosInstance.get(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleServiceId}/invoice`,
        { responseType: 'blob' }
      );
      const preview = await parseDocumentPreviewResponse(
        response,
        `customer-copy-service-${vehicleServiceId}.pdf`
      );
      setInvoicePreview(preview);
    } catch (err) {
      closeInvoicePreview();
      if (err instanceof AxiosError && err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text) as { errorMessage?: string };
          toast.error(parsed.errorMessage || 'Failed to generate customer copy', toastConfig);
          return;
        } catch {
          // fall through
        }
      }
      toast.error('Failed to generate customer copy', toastConfig);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleViewPaymentProof = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain || !vehicleService) return;
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleServiceId}/payment-proof`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Payment proof not found', toastConfig);
    }
  };

  const handleUploadPaymentProof = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain || !vehicleService || !paymentProofFile) return;
    try {
      setUploadingPaymentProof(true);
      const formData = new FormData();
      formData.append('file', paymentProofFile);
      await axiosInstance.post(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleServiceId}/payment-proof`,
        formData,
        { timeout: 120000 }
      );
      toast.success('Payment proof uploaded', toastConfig);
      setPaymentProofFile(null);
      await fetchVehicleServiceDetails();
    } catch (err) {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.errorMessage || 'Failed to upload payment proof', toastConfig);
      } else {
        toast.error('Failed to upload payment proof', toastConfig);
      }
    } finally {
      setUploadingPaymentProof(false);
    }
  };

  const paymentMethodRequiresProof =
    selectedPaymentMethod === 'ElectronicTransfer' || selectedPaymentMethod === 'Check';

  const handleMarkPaid = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain || !vehicleService) return;
    if (!selectedPaymentMethod) {
      toast.error('Select a payment method', toastConfig);
      return;
    }
    if (paymentMethodRequiresProof && !vehicleService.hasPaymentProof && !paymentProofFile) {
      toast.error('Payment proof is required for this payment method', toastConfig);
      return;
    }

    try {
      setMarkingPaid(true);

      if (paymentMethodRequiresProof && paymentProofFile && !vehicleService.hasPaymentProof) {
        const formData = new FormData();
        formData.append('file', paymentProofFile);
        await axiosInstance.post(
          `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleServiceId}/payment-proof`,
          formData,
          { timeout: 120000 }
        );
      }

      await axiosInstance.post(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleServiceId}/mark-paid`,
        { paymentMethod: selectedPaymentMethod }
      );
      toast.success('Service marked as paid', toastConfig);
      setPaymentProofFile(null);
      setSelectedPaymentMethod('');
      await fetchVehicleServiceDetails();
    } catch (err) {
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.errorMessage || 'Failed to mark as paid', toastConfig);
      } else {
        toast.error('Failed to mark as paid', toastConfig);
      }
    } finally {
      setMarkingPaid(false);
    }
  };

  // Handle edit title
  const handleEditTitle = () => {
    setEditTitle(vehicleService?.serviceTitle || '');
    setShowEditTitleModal(true);
  };

  const handleSaveTitle = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;
    const trimmed = editTitle.trim();
    if (!trimmed) {
      toast.error('Service title is required', toastConfig);
      return;
    }
    if (trimmed.length > 80) {
      toast.error('Service title cannot exceed 80 characters', toastConfig);
      return;
    }

    try {
      setUpdatingService(true);
      await axiosInstance.put(`/api/v1/${selectedBranch.branchDomain}/vehicles/0/services/${vehicleServiceId}`, {
        currentMileage: vehicleService?.currentMileage || 0,
        customerRequestComplaint: vehicleService?.customerRequestComplaint || '',
        serviceResolution: vehicleService?.serviceResolution || null,
        serviceTitle: trimmed,
        afterServiceDetails: vehicleService?.afterServiceDetails || null
      });
      toast.success('Service title updated', toastConfig);
      await fetchVehicleServiceDetails();
      setShowEditTitleModal(false);
    } catch (err) {
      console.error('Error updating service title:', err);
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.errorMessage || 'Failed to update title', toastConfig);
      } else {
        toast.error('Failed to update title', toastConfig);
      }
    } finally {
      setUpdatingService(false);
    }
  };

  const handleCancelEditTitle = () => {
    setShowEditTitleModal(false);
    setEditTitle('');
  };

  // Handle edit description
  const handleEditDescription = () => {
    setEditComplaint(vehicleService?.customerRequestComplaint || '');
    setShowEditDescriptionModal(true);
  };

  const handleSaveDescription = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;
    const plain = extractComplaintPlainText(editComplaint);
    if (!plain) {
      toast.error('Customer concern is required', toastConfig);
      return;
    }
    if (plain.length > 500) {
      toast.error('Customer concern cannot exceed 500 characters', toastConfig);
      return;
    }

    try {
      setUpdatingService(true);

      const response = await axiosInstance.put(`/api/v1/${selectedBranch.branchDomain}/vehicles/0/services/${vehicleServiceId}`, {
        currentMileage: vehicleService?.currentMileage || 0,
        customerRequestComplaint: editComplaint,
        serviceResolution: vehicleService?.serviceResolution || null,
        // Preserve existing title when only the complaint body changes.
        serviceTitle: vehicleService?.serviceTitle || undefined,
        afterServiceDetails: vehicleService?.afterServiceDetails || null
      });

      if (response.data?.complaintIsDetailedEnough === false && response.data?.complaintQualityMessage) {
        toast.warning(response.data.complaintQualityMessage, toastConfig);
      } else {
        toast.success('Customer concern updated', toastConfig);
      }

      await fetchVehicleServiceDetails();
      setShowEditDescriptionModal(false);
    } catch (err) {
      console.error('Error updating customer concern:', err);
      if (err instanceof AxiosError) {
        toast.error(err.response?.data?.errorMessage || 'Failed to update', toastConfig);
      } else {
        toast.error('Failed to update', toastConfig);
      }
    } finally {
      setUpdatingService(false);
    }
  };

  const handleCancelEditDescription = () => {
    setShowEditDescriptionModal(false);
    setEditComplaint('');
  };

  // Handle edit resolution
  const handleEditResolution = () => {
    setEditResolution(vehicleService?.serviceResolution || '');
    setShowEditResolutionModal(true);
  };

  const handleSaveResolution = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;

    try {
      setUpdatingService(true);
      
      await axiosInstance.put(`/api/v1/${selectedBranch.branchDomain}/vehicles/0/services/${vehicleServiceId}`, {
        currentMileage: vehicleService?.currentMileage || 0,
        customerRequestComplaint: vehicleService?.customerRequestComplaint || '',
        serviceResolution: editResolution || null,
        serviceTitle: vehicleService?.serviceTitle || undefined,
        afterServiceDetails: vehicleService?.afterServiceDetails || null
      });
      
      // Refresh the service details
      await fetchVehicleServiceDetails();
      setShowEditResolutionModal(false);
    } catch (err) {
      console.error('Error updating service resolution:', err);
      // You might want to show an error message to the user here
    } finally {
      setUpdatingService(false);
    }
  };

  const handleCancelEditResolution = () => {
    setShowEditResolutionModal(false);
    setEditResolution('');
  };

  // Handle edit after service details
  const handleEditAfterServiceDetails = () => {
    setEditAfterServiceDetails(vehicleService?.afterServiceDetails || '');
    setShowEditAfterServiceDetailsModal(true);
  };

  const handleSaveAfterServiceDetails = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;

    try {
      setUpdatingService(true);
      
      await axiosInstance.put(`/api/v1/${selectedBranch.branchDomain}/vehicles/0/services/${vehicleServiceId}`, {
        currentMileage: vehicleService?.currentMileage || 0,
        customerRequestComplaint: vehicleService?.customerRequestComplaint || '',
        serviceResolution: vehicleService?.serviceResolution || null,
        serviceTitle: vehicleService?.serviceTitle || undefined,
        afterServiceDetails: editAfterServiceDetails || null
      });
      
      // Refresh the service details
      await fetchVehicleServiceDetails();
      setShowEditAfterServiceDetailsModal(false);
    } catch (err) {
      console.error('Error updating after service details:', err);
      // You might want to show an error message to the user here
    } finally {
      setUpdatingService(false);
    }
  };

  const handleCancelEditAfterServiceDetails = () => {
    setShowEditAfterServiceDetailsModal(false);
    setEditAfterServiceDetails('');
  };

  // Handle edit mileage
  const handleEditMileage = () => {
    setEditMileage(vehicleService?.currentMileage || 0);
    setShowEditMileageModal(true);
  };

  // Image handling functions
  const openImageModal = (item: VehicleServiceItemDto) => {
    setSelectedItemForImages(item);
    setShowImageModal(true);
    setCameraMode(false);
    setCapturedImage(null);
    setSelectedFiles([]);
    setUploadError(null);
  };

  const startCameraPreview = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(cameras);
      
      if (cameras.length > 0) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: cameras[0].deviceId ? { exact: cameras[0].deviceId } : undefined }
        });
        setCameraStream(stream);
        setCurrentCameraId(cameras[0].deviceId);
        setCameraMode(true);
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setUploadError('Failed to start camera. Please check permissions.');
    }
  };

  const stopCameraPreview = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraMode(false);
    setCapturedImage(null);
  };

  const switchCamera = async () => {
    if (availableCameras.length <= 1) return;
    
    const currentIndex = availableCameras.findIndex(cam => cam.deviceId === currentCameraId);
    const nextIndex = (currentIndex + 1) % availableCameras.length;
    const nextCamera = availableCameras[nextIndex];
    
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: nextCamera.deviceId } }
      });
      setCameraStream(stream);
      setCurrentCameraId(nextCamera.deviceId);
    } catch (error) {
      console.error('Error switching camera:', error);
    }
  };

  const capturePhoto = async () => {
    const video = document.getElementById('cameraVideo') as HTMLVideoElement;
    if (!video || !selectedItemForImages) return;

    try {
      setUploading(true);
      setUploadError(null);

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context?.drawImage(video, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(dataUrl);

      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/jpeg', 0.8);
      });

      const file = new File([blob], `service_item_${selectedItemForImages.vehicleServiceItemId}_${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      const formData = new FormData();
      formData.append('files', file);

      await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/vehicle-services/${vehicleServiceId}/items/${selectedItemForImages.vehicleServiceItemId}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setShowImageModal(false);
      setUploadSuccess(true);
      await fetchVehicleServiceItems();
      toast.info('Images uploaded. Category analysis is running in the background…', toastConfig);

      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);

    } catch (error: unknown) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'An error occurred while uploading the image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSelectedFiles(Array.from(event.target.files));
    }
  };

  const uploadFiles = async () => {
    if (!selectedFiles.length || !selectedItemForImages) return;

    try {
      setUploading(true);
      setUploadError(null);

      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/vehicle-services/${vehicleServiceId}/items/${selectedItemForImages.vehicleServiceItemId}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setShowImageModal(false);
      setUploadSuccess(true);
      await fetchVehicleServiceItems();
      toast.info('Images uploaded. Category analysis is running in the background…', toastConfig);

      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);

    } catch (error: unknown) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'An error occurred while uploading the files');
    } finally {
      setUploading(false);
      setSelectedFiles([]);
    }
  };

  const resetImageModalState = () => {
    setSelectedFiles([]);
    setUploadError(null);
    setCameraMode(false);
    setCapturedImage(null);
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const resetImageModal = () => {
    resetImageModalState();
    setShowImageModal(false);
    setSelectedItemForImages(null);
  };
  
  // Handle image zoom / carousel
  const openImageZoom = (images: VehicleServiceItemImageDto[], startIndex = 0) => {
    if (!images.length) return;
    const safeIndex = Math.min(Math.max(startIndex, 0), images.length - 1);
    setCarouselImages(images);
    setCarouselIndex(safeIndex);
    setSelectedCategoryId(images[safeIndex]?.vehicleImageCategoryId ?? null);
    setShowZoomModal(true);
  };
  
  const closeImageZoom = () => {
    setShowZoomModal(false);
    setCarouselImages([]);
    setCarouselIndex(0);
  };

  const handleCarouselSelect = (selectedIndex: number) => {
    setCarouselIndex(selectedIndex);
    setSelectedCategoryId(carouselImages[selectedIndex]?.vehicleImageCategoryId ?? null);
  };
  
  // Handle image category
  const openCategoryModal = (image: {vehicleImageId: number, filename: string, currentCategoryId?: number}) => {
    setSelectedImageForCategory(image);
    setSelectedCategoryId(image.currentCategoryId || null);
    setShowCategoryModal(true);
  };
  
  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setSelectedImageForCategory(null);
    setSelectedCategoryId(null);
  };

  const applyCategoryToLocalImages = (vehicleImageId: number, categoryId: number | null) => {
    const categoryName = categoryId == null
      ? undefined
      : vehicleImageCategories.find(c => c.vehicleImageCategoryId === categoryId)?.vehicleImageCategoryName;

    const patch = (img: VehicleServiceItemImageDto): VehicleServiceItemImageDto =>
      img.vehicleImageId === vehicleImageId
        ? {
            ...img,
            vehicleImageCategoryId: categoryId ?? undefined,
            vehicleImageCategoryName: categoryName
          }
        : img;

    setCarouselImages(prev => prev.map(patch));
    setVehicleProfileImages(prev => prev.map(patch));
  };
  
  const handleSetImageCategory = async (vehicleImageId?: number) => {
    const imageId = vehicleImageId
      ?? selectedImageForCategory?.vehicleImageId
      ?? carouselImages[carouselIndex]?.vehicleImageId;
    if (!imageId || !selectedBranch?.branchDomain) return;
    
    try {
      setUpdatingCategory(true);
      
      await axiosInstance.put(`/api/v1/vehicleImages/${imageId}/setCategory`, {
        vehicleImageCategoryId: selectedCategoryId
      });
      
      await fetchVehicleServiceItems();
      if (vehicleService?.vehicleId) {
        await fetchVehicleProfileImage(vehicleService.vehicleId);
      }
      applyCategoryToLocalImages(imageId, selectedCategoryId);

      if (showCategoryModal) {
        closeCategoryModal();
      }
      
      toast.success('Image category updated', toastConfig);
      
    } catch (err) {
      console.error('Error setting image category:', err);
      toast.error('Failed to update image category', toastConfig);
    } finally {
      setUpdatingCategory(false);
    }
  };

  const handleSaveMileage = async () => {
    if (!vehicleServiceId || !selectedBranch?.branchDomain) return;

    try {
      setUpdatingService(true);
      
      await axiosInstance.put(`/api/v1/${selectedBranch.branchDomain}/vehicles/0/services/${vehicleServiceId}`, {
        currentMileage: editMileage,
        customerRequestComplaint: vehicleService?.customerRequestComplaint || '',
        serviceResolution: vehicleService?.serviceResolution || null,
        serviceTitle: vehicleService?.serviceTitle || undefined,
        afterServiceDetails: vehicleService?.afterServiceDetails || null
      });
      
      // Refresh the service details
      await fetchVehicleServiceDetails();
      setShowEditMileageModal(false);
    } catch (err) {
      console.error('Error updating mileage:', err);
      // You might want to show an error message to the user here
    } finally {
      setUpdatingService(false);
    }
  };

  const handleCancelEditMileage = () => {
    setShowEditMileageModal(false);
    setEditMileage(0);
  };

  useEffect(() => {
    if (selectedBranch?.branchDomain) {
      fetchVehicleServiceDetails();
      fetchVehicleImageCategories();
    }
  }, [vehicleServiceId, selectedBranch?.branchDomain]);

  useEffect(() => {
    if (!selectedBranch?.branchDomain) return;
    let cancelled = false;
    axiosInstance
      .get<BranchMechanic[]>(`/api/v1/${selectedBranch.branchDomain}/mechanics`)
      .then(response => {
        if (!cancelled) setMechanics(response.data);
      })
      .catch(() => {
        if (!cancelled) setMechanics([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBranch?.branchDomain]);

  const mechanicChoices = (selectedId?: number | null, selectedName?: string | null) => {
    if (selectedId && !mechanics.some(mechanic => mechanic.userId === selectedId)) {
      return [{ userId: selectedId, name: selectedName || 'Assigned mechanic' }, ...mechanics];
    }
    return mechanics;
  };

  const serviceMechanicOptions = () => {
    const extras = (laborEditItem?.mechanics ?? []).filter(
      mechanic => !mechanics.some(choice => choice.userId === mechanic.userId)
    );
    return [...extras, ...mechanics];
  };

  const openEditLead = () => {
    setEditLeadId(vehicleService?.actingLeadMechanicUserId ?? '');
    setShowEditLeadModal(true);
  };

  const openEditReferrer = () => {
    setEditReferrer(vehicleService?.referrer ?? '');
    setShowEditReferrerModal(true);
  };

  const openEditDropOff = () => {
    const dropOff = splitServiceClock(vehicleService?.droppedOffOn);
    setEditDropOffDate(dropOff.date);
    setEditDropOffTime(dropOff.time);
    setShowEditDropOffModal(true);
  };

  const saveDropOff = async () => {
    if (!vehicleService || !selectedBranch?.branchDomain) return;
    const droppedOffOn = joinServiceClock(editDropOffDate, editDropOffTime);
    try {
      setSavingDropOff(true);
      const response = await axiosInstance.put<{ droppedOffOn?: string | null }>(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleService.vehicleServiceId}/drop-off`,
        { droppedOffOn: droppedOffOn || null }
      );
      setVehicleService(current => current ? { ...current, ...response.data } : current);
      setShowEditDropOffModal(false);
      toast.success('Drop-off updated', toastConfig);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the drop-off'), toastConfig);
    } finally {
      setSavingDropOff(false);
    }
  };

  const saveReferrer = async () => {
    if (!vehicleService || !selectedBranch?.branchDomain) return;
    try {
      setSavingReferrer(true);
      const response = await axiosInstance.put<{ referrer?: string | null }>(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleService.vehicleServiceId}/referrer`,
        { referrer: editReferrer.trim() || null }
      );
      setVehicleService(current => current ? { ...current, ...response.data } : current);
      setShowEditReferrerModal(false);
      toast.success('Referrer updated', toastConfig);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the referrer'), toastConfig);
    } finally {
      setSavingReferrer(false);
    }
  };

  const saveActingLead = async () => {
    if (!vehicleService || !selectedBranch?.branchDomain) return;
    try {
      setSavingLead(true);
      const response = await axiosInstance.put<{
        actingLeadMechanicUserId?: number | null;
        actingLeadMechanicName?: string | null;
      }>(
        `/api/v1/${selectedBranch.branchDomain}/vehicles/${vehicleService.vehicleId}/services/${vehicleService.vehicleServiceId}/acting-lead`,
        { mechanicUserId: editLeadId === '' ? null : editLeadId }
      );
      setVehicleService(current => current ? { ...current, ...response.data } : current);
      setShowEditLeadModal(false);
      toast.success('Acting lead mechanic updated', toastConfig);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the acting lead mechanic'), toastConfig);
    } finally {
      setSavingLead(false);
    }
  };

  const openEditLabor = (item: VehicleServiceItemDto) => {
    const start = splitServiceClock(item.startedOn);
    const end = splitServiceClock(item.endedOn);
    setLaborEditItem(item);
    setEditLaborMechanicIds((item.mechanics ?? []).map(mechanic => mechanic.userId));
    setEditLaborStartDate(start.date);
    setEditLaborStartTime(start.time);
    setEditLaborEndDate(end.date);
    setEditLaborEndTime(end.time);
  };

  const saveLabor = async () => {
    if (!selectedBranch?.branchDomain || !laborEditItem) return;
    const startedOn = joinServiceClock(editLaborStartDate, editLaborStartTime);
    const endedOn = joinServiceClock(editLaborEndDate, editLaborEndTime);
    if (startedOn && endedOn && endedOn < startedOn) {
      toast.error('End time must be at or after the start time', toastConfig);
      return;
    }
    try {
      setSavingLabor(true);
      const response = await axiosInstance.put<VehicleServiceItemDto>(
        `/api/v1/${selectedBranch.branchDomain}/vehicle-services/${laborEditItem.vehicleServiceId}/items/${laborEditItem.vehicleServiceItemId}/labor`,
        {
          mechanicUserIds: editLaborMechanicIds,
          startedOn: startedOn || null,
          endedOn: endedOn || null
        }
      );
      setItems(current => current.map(existing =>
        existing.vehicleServiceItemId === response.data.vehicleServiceItemId ? response.data : existing
      ));
      setLaborEditItem(null);
      toast.success('Service updated', toastConfig);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the mechanic or times'), toastConfig);
    } finally {
      setSavingLabor(false);
    }
  };

  const hasPendingCategoryAnalysis = items.some(item =>
    (item.vehicleServiceItemImages ?? []).some(img => !!img.isCategoryAnalysisPending)
  );

  useEffect(() => {
    if (!hasPendingCategoryAnalysis || !selectedBranch?.branchDomain) return;

    const timer = window.setInterval(() => {
      void fetchVehicleServiceItems({ silent: true });
    }, 4000);

    return () => window.clearInterval(timer);
  }, [hasPendingCategoryAnalysis, selectedBranch?.branchDomain, vehicleServiceId]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading vehicle service details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <strong>Error:</strong> {error}
        </Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!vehicleService) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">Vehicle service not found</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const totalAmount = items
    .filter(item => !item.isDeleted)
    .reduce((sum, item) => sum + item.amount, 0);
  const vehicle = vehicleService.vehicle;
  const vehicleDisplayName = vehicle.plateNumber || vehicle.conductionNumber || `Vehicle #${vehicleService.vehicleId}`;
  const vehicleSpecs = [vehicle.year, vehicle.carMake, vehicle.carModel].filter(Boolean).join(' ');

  return (
    <div className="container-fluid">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        {/* Car profile */}
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faCar} className="me-2" />
                Vehicle
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-column align-items-center align-items-lg-start gap-3">
                <div
                  className="bg-light border rounded overflow-hidden d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 160,
                    height: 160,
                    cursor: vehicleProfileImages.length ? 'zoom-in' : 'default'
                  }}
                  onClick={() => {
                    if (vehicleProfileImages.length) {
                      openImageZoom(vehicleProfileImages, 0);
                    }
                  }}
                  title={vehicleProfileImages.length ? 'View vehicle photo' : 'No vehicle photo'}
                >
                  {vehicleProfileImageId ? (
                    <RetryImage
                      googleFileId={vehicleProfileImageId}
                      alt={vehicleDisplayName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <FontAwesomeIcon icon={faCar} size="3x" className="text-muted" />
                  )}
                </div>
                <div className="w-100 text-center text-lg-start">
                  <h3 className="mb-1">
                    <Link
                      to={`/vehicles/${vehicleService.vehicleId}`}
                      className="text-decoration-none text-body"
                    >
                      {vehicleDisplayName}
                    </Link>
                  </h3>
                  {vehicleSpecs && (
                    <p className="text-muted mb-2">{vehicleSpecs}</p>
                  )}
                  <div className="text-body-secondary small">
                    {vehicle.customerName && (
                      <div className="mb-1"><strong>Customer:</strong> {vehicle.customerName}</div>
                    )}
                    <div className="mb-1 d-flex align-items-start justify-content-between gap-2">
                      <div>
                        <strong>Referrer:</strong>{' '}
                        {vehicleService.referrer?.trim()
                          ? vehicleService.referrer
                          : <span className="text-muted">Not set</span>}
                      </div>
                      {vehicleService.status !== 'Submitted' && canEditService && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-shrink-0"
                          title="Edit referrer"
                          onClick={openEditReferrer}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                    </div>
                    {vehicle.conductionNumber && vehicle.plateNumber && (
                      <div className="mb-1"><strong>Conduction:</strong> {vehicle.conductionNumber}</div>
                    )}
                    {(vehicle.bodyType || vehicle.color || vehicle.fuelType || vehicle.transmission) && (
                      <div>
                        {[carModelBodyTypeLabel(vehicle.bodyType), vehicle.color, vehicle.fuelType, vehicle.transmission]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Service Details */}
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faWrench} className="me-2" />
                  Service Details
                </h5>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {vehicleService.status !== 'Submitted' && canEditService && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleSubmitService}
                      disabled={submittingService}
                    >
                      {submittingService ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Submitting...
                        </>
                      ) : (
                        'Confirm Service'
                      )}
                    </Button>
                  )}
                  {vehicleService.status === 'Submitted' && vehicleService.hasServiceSummary && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => void handleViewSummary()}
                    >
                      <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                      View summary
                    </Button>
                  )}
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => void handleDownloadInvoice()}
                    disabled={downloadingInvoice}
                    title="View PDF"
                  >
                    {downloadingInvoice ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Preparing…
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                        PDF
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Table borderless size="sm" className="mb-0">
                <tbody>
                  <tr>
                    <td style={{ width: '220px' }}><strong>Service Date:</strong></td>
                    <td>{new Date(vehicleService.createdOn).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <td><strong>Drop-off:</strong></td>
                    <td>
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <div className="flex-grow-1">
                          {formatServiceClock(vehicleService.droppedOffOn)
                            || <span className="text-muted">Not set</span>}
                        </div>
                        {vehicleService.status !== 'Submitted' && canEditService && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="flex-shrink-0"
                            title="Edit drop-off"
                            onClick={openEditDropOff}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                <tr>
                  <td><strong>Mileage:</strong></td>
                  <td>
                    {vehicleService.status !== 'Submitted' && canEditService && (
                      <div className="d-flex align-items-center">
                        <Badge bg={vehicleService.currentMileage <= 0 ? 'warning' : 'secondary'} className="px-2 py-1 me-2">
                          {vehicleService.currentMileage <= 0 && (
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                          )}
                          {vehicleService.currentMileage.toLocaleString()} km
                        </Badge>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleEditMileage}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </div>
                    )}
                    {(vehicleService.status === 'Submitted' || !canEditService) && (
                      <Badge bg={vehicleService.currentMileage <= 0 ? 'warning' : 'secondary'} className="px-2 py-1">
                        {vehicleService.currentMileage <= 0 && (
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                        )}
                        {vehicleService.currentMileage.toLocaleString()} km
                      </Badge>
                    )}
                    {vehicleService.currentMileage <= 0 && (
                      <div className="small text-warning mt-1">
                        Mileage is empty or zero.
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td><strong>Title:</strong></td>
                  <td>
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="flex-grow-1">
                        {vehicleService.serviceTitle?.trim()
                          ? vehicleService.serviceTitle
                          : <span className="text-muted">No title</span>}
                      </div>
                      {vehicleService.status !== 'Submitted' && canEditService && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-shrink-0"
                          onClick={handleEditTitle}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td><strong>Acting lead mechanic:</strong></td>
                  <td>
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="flex-grow-1">
                        {vehicleService.actingLeadMechanicName?.trim()
                          ? vehicleService.actingLeadMechanicName
                          : <span className="text-muted">Not assigned</span>}
                      </div>
                      {vehicleService.status !== 'Submitted' && canEditService && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-shrink-0"
                          onClick={openEditLead}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td><strong>Customer concern:</strong></td>
                  <td>
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="flex-grow-1">
                        <RichHtmlContent
                          html={vehicleService.customerRequestComplaint}
                          empty="No customer concern provided"
                        />
                      </div>
                      {vehicleService.status !== 'Submitted' && canEditService && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-shrink-0"
                          onClick={handleEditDescription}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td><strong>Resolution:</strong></td>
                  <td>
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="flex-grow-1">
                        <RichHtmlContent
                          html={vehicleService.serviceResolution}
                          empty="No resolution provided"
                        />
                      </div>
                      {vehicleService.status !== 'Submitted' && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="flex-shrink-0"
                          onClick={handleEditResolution}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {vehicleService.status === 'Submitted' && (
                  <tr>
                    <td><strong>After Service Details:</strong></td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="me-2">{vehicleService.afterServiceDetails || 'No after service details provided'}</span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleEditAfterServiceDetails}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                <tr>
                  <td><strong>Status:</strong></td>
                  <td>
                    <Badge 
                      bg={getStatusDisplay(vehicleService.status).color}
                      className="px-2 py-1 me-2"
                    >
                      {getStatusDisplay(vehicleService.status).text}
                    </Badge>
                    {vehicleService.status === 'Submitted' && (
                      <Badge bg={vehicleService.paymentStatus === 'Paid' ? 'success' : 'secondary'} className="px-2 py-1">
                        {vehicleService.paymentStatus === 'Paid'
                          ? `Paid${vehicleService.paymentMethodDisplay ? ` · ${vehicleService.paymentMethodDisplay}` : ''}`
                          : 'Unpaid'}
                      </Badge>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>

            {vehicleService.status === 'Submitted' && canManageAccounting && (
              <Card className="mt-3 border">
                <Card.Body>
                  <h6 className="mb-3">Accounting</h6>
                  {vehicleService.hasPaymentProof && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2 mb-2"
                      onClick={() => void handleViewPaymentProof()}
                    >
                      View payment proof
                    </Button>
                  )}
                  {vehicleService.paymentStatus !== 'Paid' && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Payment method <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={selectedPaymentMethod}
                          onChange={(e) => {
                            const next = (e.target.value || '') as VehicleServicePaymentMethod | '';
                            setSelectedPaymentMethod(next);
                            if (next !== 'ElectronicTransfer' && next !== 'Check') {
                              setPaymentProofFile(null);
                            }
                          }}
                        >
                          <option value="">Select method</option>
                          <option value="Cash">Cash</option>
                          <option value="ElectronicTransfer">GCash / Maya / Bank transfer</option>
                          <option value="Card">Card</option>
                          <option value="Check">Check</option>
                        </Form.Select>
                      </Form.Group>

                      {paymentMethodRequiresProof && (
                        <Form.Group className="mb-2">
                          <Form.Label>
                            Upload payment proof <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => {
                              const input = e.target as HTMLInputElement;
                              setPaymentProofFile(input.files?.[0] ?? null);
                            }}
                          />
                          <Form.Text className="text-muted">
                            Required for GCash / Maya / bank transfer and check.
                          </Form.Text>
                        </Form.Group>
                      )}

                      <div className="d-flex gap-2">
                        {paymentMethodRequiresProof && !vehicleService.hasPaymentProof && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!paymentProofFile || uploadingPaymentProof}
                            onClick={() => void handleUploadPaymentProof()}
                          >
                            {uploadingPaymentProof ? 'Uploading…' : 'Upload proof'}
                          </Button>
                        )}
                        <Button
                          variant="success"
                          size="sm"
                          disabled={
                            markingPaid
                            || !selectedPaymentMethod
                            || (paymentMethodRequiresProof && !vehicleService.hasPaymentProof && !paymentProofFile)
                          }
                          onClick={() => void handleMarkPaid()}
                        >
                          {markingPaid ? 'Saving…' : 'Mark as paid'}
                        </Button>
                      </div>
                    </>
                  )}
                  {vehicleService.paymentStatus === 'Paid' && vehicleService.paymentMethodDisplay && (
                    <div className="text-muted small mt-2">
                      Paid via {vehicleService.paymentMethodDisplay}
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
        </Col>
      </Row>

      {vehicleService.status === 'Submitted' && (
        <Alert variant="success" className="mb-4">
          <Alert.Heading>Service Submitted Successfully!</Alert.Heading>
          <p className="mb-0">
            This service has been processed and all items have been deducted from inventory.
            No further modifications can be made to the service items.
          </p>
        </Alert>
      )}

      {uploadSuccess && (
        <Alert variant="success" dismissible onClose={() => setUploadSuccess(false)} className="mb-4">
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          Images uploaded. AI is analyzing categories in the background…
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Service Items</h5>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddItemModal(true)}
              disabled={vehicleService?.status === 'Submitted'}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Item
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {vehicleService.status !== 'Submitted' && (
            <Alert variant="warning" className="mb-3 py-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Product items usually need Old Parts, New Parts, and Side-by-side photos.
                  Oils and fluids only need a New Parts photo.
            </Alert>
            )}
            {hasPendingCategoryAnalysis && (
              <Alert variant="info" className="mb-3 py-2">
                <Spinner animation="border" size="sm" className="me-2" />
                AI is analyzing uploaded photo categories…
              </Alert>
            )}
            {itemsMissingRequiredImages.length > 0 && vehicleService.status !== 'Submitted' && (
              <Alert variant="danger" className="mb-3 py-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {itemsMissingRequiredImages.length} product item{itemsMissingRequiredImages.length === 1 ? '' : 's'} missing required photos.
              </Alert>
            )}
            {itemsLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading items...</span>
              </div>
            ) : items.length > 0 ? (
              <>
                <div className="table-responsive">
                  <Table hover className="mb-0">
                                           <thead className="table-light">
                       <tr>
                         <th style={{ width: '90px' }}>Photos</th>
                         <th>Product</th>
                         <th style={{ width: '100px' }}>Quantity</th>
                         <th style={{ width: '100px' }}>Unit</th>
                         <th style={{ width: '120px' }}>Unit Price</th>
                         <th style={{ width: '120px' }}>Amount</th>
                         <th style={{ width: '120px' }}>Action</th>
                       </tr>
                     </thead>
                    <tbody>
                      {items.map((item) => {
                        const images = item.vehicleServiceItemImages ?? [];
                        const missing = item.missingRequiredImageTypes ?? [];
                        const isDeleted = !!item.isDeleted;
                        const showWarning = !isDeleted && !!item.imagesRequired && missing.length > 0;
                        const stackPreview = images.slice(0, 3);
                        const analyzingCount = images.filter(img => img.isCategoryAnalysisPending).length;
                        const strikeStyle = isDeleted
                          ? { textDecoration: 'line-through', opacity: 0.55 }
                          : undefined;

                        return (
                        <tr key={item.vehicleServiceItemId} className={isDeleted ? 'text-body-tertiary' : undefined}>
                          <td style={strikeStyle}>
                            {images.length > 0 ? (
                              <div
                                className="position-relative"
                                style={{ width: 56, height: 48, cursor: 'pointer' }}
                                onClick={() => openImageZoom(images, 0)}
                                title={analyzingCount > 0 ? 'View photos (analysis in progress)' : 'View photos'}
                              >
                                {stackPreview.map((img, idx) => (
                                  <div
                                    key={img.googleFileId}
                                    className="position-absolute border rounded overflow-hidden bg-white"
                                    style={{
                                      width: 40,
                                      height: 40,
                                      left: idx * 8,
                                      top: idx * 2,
                                      zIndex: stackPreview.length - idx,
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                      opacity: img.isCategoryAnalysisPending ? 0.7 : 1
                                    }}
                                  >
                                    <RetryImage
                                      googleFileId={img.googleFileId}
                                      alt={img.filename}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  </div>
                                ))}
                                {analyzingCount > 0 ? (
                                  <Badge
                                    bg="warning"
                                    text="dark"
                                    className="position-absolute"
                                    style={{ right: -8, top: -4, fontSize: '0.6rem', zIndex: 10 }}
                                    title="Category analysis in progress"
                                  >
                                    <Spinner animation="border" size="sm" className="me-1" style={{ width: 8, height: 8 }} />
                                    AI
                                  </Badge>
                                ) : images.length > 3 ? (
                                  <Badge
                                    bg="dark"
                                    className="position-absolute"
                                    style={{ right: -4, bottom: -4, fontSize: '0.65rem' }}
                                  >
                                    +{images.length - 3}
                                  </Badge>
                                ) : null}
                              </div>
                            ) : null}
                          </td>
                          <td style={strikeStyle}>
                            <div className="d-flex align-items-start gap-2">
                              {showWarning && (
                                <FontAwesomeIcon
                                  icon={faExclamationTriangle}
                                  className="text-warning mt-1"
                                  title={`Missing: ${missing.join(', ')}`}
                                />
                              )}
                              <div>
                                {item.productId ? (
                                  <Link 
                                    to={`/product/${selectedBranch?.branchDomain}/${item.productId}`}
                                    className="text-decoration-none"
                                    style={strikeStyle}
                                  >
                                    <strong>{item.productName}</strong>
                                  </Link>
                                ) : (
                                  <strong>{item.noIdProductName}</strong>
                                )}
                                {isDeleted && (
                                  <Badge bg="secondary" className="ms-2">Removed</Badge>
                                )}
                                {item.productId && (
                                  <div className="text-muted small">
                                    Product ID: {item.productId}
                                  </div>
                                )}
                                {item.priceOverrideReason && (
                                  <div className="small text-warning">
                                    Price changed: {item.priceOverrideReason}
                                  </div>
                                )}
                                {item.shopServiceId != null && !isDeleted && (
                                  <div className="d-flex align-items-start justify-content-between gap-2 mt-2">
                                    <div className="small text-body-secondary">
                                      <div>Mechanics: {(item.mechanics ?? []).map(mechanic => mechanic.name).filter(Boolean).join(', ') || 'Not assigned'}</div>
                                      <div>Start: {formatServiceClock(item.startedOn) || 'Not set'}</div>
                                      <div>End: {formatServiceClock(item.endedOn) || 'Not set'}</div>
                                    </div>
                                    {vehicleService?.status !== 'Submitted' && canEditService && (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="flex-shrink-0"
                                        onClick={() => openEditLabor(item)}
                                        title="Edit mechanics and times"
                                      >
                                        <FontAwesomeIcon icon={faEdit} />
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {showWarning && (
                                  <div className="small text-warning">
                                    Missing: {missing.join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={strikeStyle}>{item.quantity}</td>
                          <td style={strikeStyle}>
                            <Badge bg="light" text="dark">
                              {unitOptions.find(u => u.value === item.unitOfMeasure)?.label || item.unitOfMeasure}
                            </Badge>
                          </td>
                          <td style={strikeStyle}>₱{item.unitPrice.toLocaleString()}</td>
                           <td style={strikeStyle}>
                             <strong>₱{item.amount.toLocaleString()}</strong>
                           </td>
                           <td>
                            {!isDeleted && (
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => openImageModal(item)}
                                title="Manage Images"
                              >
                                <FontAwesomeIcon icon={faImage} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => openRemoveItemModal(item.vehicleServiceItemId)}
                                disabled={vehicleService?.status === 'Submitted'}
                                title="Remove Item"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                                           <tfoot>
                       <tr>
                         <td colSpan={5} className="text-end"><strong>Total:</strong></td>
                         <td><strong>₱{totalAmount.toLocaleString()}</strong></td>
                         <td></td>
                       </tr>
                     </tfoot>
                  </Table>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-muted">
                <p>No items added to this service yet.</p>
              </div>
            )}
          </Card.Body>
        </Card>

      {/* Add Item Modal */}
      <Modal show={showAddItemModal} onHide={() => setShowAddItemModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Service Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            handleAddItem();
          }}>
            <ButtonGroup className="mb-3">
              <Button
                type="button"
                variant={itemMode === 'part' ? 'primary' : 'outline-primary'}
                onClick={() => {
                  setItemMode('part');
                  setSelectedService(null);
                }}
              >
                Part
              </Button>
              <Button
                type="button"
                variant={itemMode === 'service' ? 'primary' : 'outline-primary'}
                onClick={() => {
                  setItemMode('service');
                  setBarcodeInput('');
                  setBarcodeProduct(null);
                  setFromBarcodeDetails(false);
                  setItemFormData(current => ({
                    ...current,
                    productId: undefined,
                    procurementInvoiceId: undefined
                  }));
                }}
              >
                Service
              </Button>
            </ButtonGroup>

            {itemMode === 'service' && (
              <div className="mb-3">
                <div className="small text-body-secondary mb-2">
                  {serviceQuotes?.priceTier
                    ? `${serviceQuotes.priceTier}${serviceQuotes.bodyType ? ` · ${carModelBodyTypeLabel(serviceQuotes.bodyType)}` : ''}${serviceQuotes.carModelName ? ` · ${serviceQuotes.carModelName}` : ''}`
                    : 'No set price tier for this vehicle. Enter the price.'}
                </div>
                <SearchBox
                  placeholder="Search services"
                  value={serviceQuery}
                  onChange={event => setServiceQuery(event.target.value)}
                />
                <div className="border rounded mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {filteredServices.slice(0, 40).map(service => (
                    <button
                      key={service.shopServiceId}
                      type="button"
                      className={`d-block w-100 text-start border-0 px-3 py-2 ${selectedService?.shopServiceId === service.shopServiceId ? 'bg-primary-subtle' : 'bg-transparent'}`}
                      onClick={() => chooseService(service)}
                    >
                      <strong>{service.code}</strong> {service.name}
                      <span className="text-body-tertiary"> · {service.categoryName}</span>
                      {service.standardPrice != null && (
                        <span className="float-end">{`₱${service.standardPrice.toLocaleString()}`}</span>
                      )}
                    </button>
                  ))}
                  {filteredServices.length === 0 && (
                    <div className="text-body-tertiary px-3 py-2">No matching services.</div>
                  )}
                </div>
                {selectedService && selectedService.minPrice != null && selectedService.maxPrice != null && (
                  <div className="small mt-2">
                    Allowed ₱{selectedService.minPrice.toLocaleString()}–₱{selectedService.maxPrice.toLocaleString()}
                    {selectedService.isCarModelPrice ? ' (this car model)' : ''}
                  </div>
                )}
                {selectedService && selectedService.standardPrice == null && (
                  <div className="small mt-2">No set price for this vehicle.</div>
                )}
              </div>
            )}

            {/* Barcode Scanner */}
            {itemMode === 'part' && (
            <Form.Group className="mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faBarcode} className="me-2" />
                Scan Barcode (optional)
              </Form.Label>
              <SearchBox
                placeholder="Barcode"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onBlur={(e) => handleBarcodeChange(e.target.value)}
              />
            </Form.Group>
            )}

            {/* Product Name */}
            <Form.Group className="mb-3">
              <Form.Label>Product Name</Form.Label>
              <Form.Control
                type="text"
                value={fromBarcodeDetails ? (barcodeProduct?.productName || '') : itemFormData.noIdProductName}
                onChange={(e) => {
                  if (!fromBarcodeDetails) {
                    setItemFormData({
                      ...itemFormData,
                      noIdProductName: e.target.value
                    });
                  }
                }}
                disabled={fromBarcodeDetails || itemMode === 'service'}
                required={itemMode === 'part' && !fromBarcodeDetails}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={itemFormData.quantity}
                    onChange={(e) => setItemFormData({
                      ...itemFormData,
                      quantity: parseFloat(e.target.value) || 0
                    })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit of Measure</Form.Label>
                  <Form.Select
                    value={itemFormData.unitOfMeasure}
                    onChange={(e) => setItemFormData({
                      ...itemFormData,
                      unitOfMeasure: parseInt(e.target.value) as UnitOfMeasure
                    })}
                    required
                  >
                    {unitOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Unit Price</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={itemFormData.unitPrice}
                onChange={(e) => setItemFormData({
                  ...itemFormData,
                  unitPrice: parseInt(e.target.value) || 0
                })}
                required
              />
            </Form.Group>

            {itemMode === 'service' && serviceOutsideBand && (
              <Form.Group className="mb-3">
                <Form.Label>Reason for price change</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={255}
                  value={itemFormData.priceOverrideReason ?? ''}
                  onChange={event => setItemFormData({
                    ...itemFormData,
                    priceOverrideReason: event.target.value
                  })}
                  required
                />
              </Form.Group>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button 
                variant="secondary" 
                onClick={() => setShowAddItemModal(false)}
                disabled={addingItem}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={addingItem || (itemMode === 'part' && !itemFormData.productId && !itemFormData.noIdProductName) || (itemMode === 'service' && !selectedService) || (itemMode === 'service' && serviceOutsideBand && !itemFormData.priceOverrideReason?.trim())}
              >
                {addingItem ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  'Add Item'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Title Modal */}
      <Modal show={showEditTitleModal} onHide={handleCancelEditTitle} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Service Title</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            void handleSaveTitle();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                maxLength={80}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                disabled={updatingService}
                placeholder="Short title for this service"
              />
              <Form.Text className="text-muted">{editTitle.trim().length}/80</Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCancelEditTitle} disabled={updatingService}>
                Cancel
              </Button>
              <Button
                variant="success"
                type="submit"
                disabled={updatingService || !editTitle.trim()}
              >
                {updatingService ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Customer concern Modal */}
      <Modal show={showEditDescriptionModal} onHide={handleCancelEditDescription} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Customer concern</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            void handleSaveDescription();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Customer concern</Form.Label>
              <ComplaintEditor
                value={editComplaint}
                onChange={(html: string) => setEditComplaint(html)}
                maxPlainTextLength={500}
                disabled={updatingService}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCancelEditDescription} disabled={updatingService}>
                Cancel
              </Button>
              <Button
                variant="success"
                type="submit"
                disabled={
                  updatingService
                  || !extractComplaintPlainText(editComplaint)
                  || extractComplaintPlainText(editComplaint).length > 500
                }
              >
                {updatingService ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Resolution Modal */}
      <Modal show={showEditResolutionModal} onHide={handleCancelEditResolution} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Service Resolution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            void handleSaveResolution();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Service Resolution</Form.Label>
              <ComplaintEditor
                value={editResolution}
                onChange={(html: string) => setEditResolution(html)}
                disabled={updatingService}
                placeholder="Describe the service resolution..."
                height={260}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCancelEditResolution} disabled={updatingService}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={updatingService}>
                {updatingService ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit After Service Details Modal */}
      <Modal show={showEditAfterServiceDetailsModal} onHide={handleCancelEditAfterServiceDetails} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit After Service Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            handleSaveAfterServiceDetails();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>After Service Details</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editAfterServiceDetails}
                onChange={(e) => setEditAfterServiceDetails(e.target.value)}
                disabled={updatingService}
                placeholder="Enter after service details..."
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCancelEditAfterServiceDetails} disabled={updatingService}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={updatingService}>
                {updatingService ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Drop-off Modal */}
      <Modal show={showEditDropOffModal} onHide={() => { if (!savingDropOff) setShowEditDropOffModal(false); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Drop-off</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(event) => {
            event.preventDefault();
            void saveDropOff();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Drop-off</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="date"
                  value={editDropOffDate}
                  disabled={savingDropOff}
                  onChange={event => setEditDropOffDate(event.target.value)}
                />
                <Form.Control
                  type="time"
                  value={editDropOffTime}
                  disabled={savingDropOff}
                  onChange={event => setEditDropOffTime(event.target.value)}
                />
              </div>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowEditDropOffModal(false)} disabled={savingDropOff}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={savingDropOff}>
                {savingDropOff ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Referrer Modal */}
      <Modal show={showEditReferrerModal} onHide={() => { if (!savingReferrer) setShowEditReferrerModal(false); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Referrer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(event) => {
            event.preventDefault();
            void saveReferrer();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Referrer</Form.Label>
              <Form.Control
                type="text"
                value={editReferrer}
                maxLength={100}
                disabled={savingReferrer}
                placeholder="Who referred this customer?"
                onChange={event => setEditReferrer(event.target.value)}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowEditReferrerModal(false)} disabled={savingReferrer}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={savingReferrer}>
                {savingReferrer ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Acting Lead Modal */}
      <Modal show={showEditLeadModal} onHide={() => setShowEditLeadModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Acting Lead Mechanic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(event) => {
            event.preventDefault();
            void saveActingLead();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Acting lead mechanic</Form.Label>
              <Form.Select
                value={editLeadId}
                disabled={savingLead}
                onChange={event => setEditLeadId(event.target.value ? Number(event.target.value) : '')}
              >
                <option value="">Not assigned</option>
                {mechanicChoices(editLeadId === '' ? null : editLeadId, vehicleService?.actingLeadMechanicName).map(mechanic => (
                  <option key={mechanic.userId} value={mechanic.userId}>{mechanic.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowEditLeadModal(false)} disabled={savingLead}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={savingLead}>
                {savingLead ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Service Mechanic and Times Modal */}
      <Modal show={laborEditItem != null} onHide={() => { if (!savingLabor) setLaborEditItem(null); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit {laborEditItem?.noIdProductName || 'Service'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(event) => {
            event.preventDefault();
            void saveLabor();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Mechanics</Form.Label>
              <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                {serviceMechanicOptions().length === 0 && (
                  <div className="text-body-secondary small">No mechanics at this branch.</div>
                )}
                {serviceMechanicOptions().map(mechanic => (
                  <Form.Check
                    key={mechanic.userId}
                    id={`service-mechanic-${mechanic.userId}`}
                    type="checkbox"
                    label={mechanic.name}
                    className="mb-1"
                    checked={editLaborMechanicIds.includes(mechanic.userId)}
                    disabled={savingLabor}
                    onChange={event => {
                      setEditLaborMechanicIds(current => event.target.checked
                        ? [...current, mechanic.userId]
                        : current.filter(id => id !== mechanic.userId));
                    }}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="date"
                  value={editLaborStartDate}
                  disabled={savingLabor}
                  onChange={event => setEditLaborStartDate(event.target.value)}
                />
                <Form.Control
                  type="time"
                  value={editLaborStartTime}
                  disabled={savingLabor}
                  onChange={event => setEditLaborStartTime(event.target.value)}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="date"
                  value={editLaborEndDate}
                  disabled={savingLabor}
                  onChange={event => setEditLaborEndDate(event.target.value)}
                />
                <Form.Control
                  type="time"
                  value={editLaborEndTime}
                  disabled={savingLabor}
                  onChange={event => setEditLaborEndTime(event.target.value)}
                />
              </div>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setLaborEditItem(null)} disabled={savingLabor}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={savingLabor}>
                {savingLabor ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Mileage Modal */}
      <Modal show={showEditMileageModal} onHide={handleCancelEditMileage} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Current Mileage</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            handleSaveMileage();
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Current Mileage (km)</Form.Label>
              <Form.Control
                type="number"
                value={editMileage}
                onChange={(e) => setEditMileage(parseInt(e.target.value) || 0)}
                disabled={updatingService}
                min="0"
                step="1"
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCancelEditMileage} disabled={updatingService}>
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={updatingService}>
                {updatingService ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Image Upload Modal */}
      <Modal show={showImageModal} onHide={resetImageModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faImage} className="me-2" />
            Manage Images for {selectedItemForImages?.productName || selectedItemForImages?.noIdProductName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadError && (
            <Alert variant="danger" dismissible onClose={() => setUploadError(null)} className="mb-3">
              <strong>Error:</strong> {uploadError}
            </Alert>
          )}

          {/* Camera Preview */}
          {cameraMode && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Camera Preview</h6>
                {availableCameras.length > 1 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={switchCamera}
                    disabled={uploading}
                  >
                    <FontAwesomeIcon icon={faSync} className="me-2" />
                    Switch Camera
                  </Button>
                )}
              </div>
              <div className="text-center">
                {capturedImage ? (
                  <>
                    <img
                      src={capturedImage}
                      alt="Captured"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6'
                      }}
                    />
                    <div className="mt-3 text-muted">
                      {uploading ? 'Uploading photo...' : 'Photo captured and uploading...'}
                    </div>
                  </>
                ) : (
                  <>
                    <video
                      id="cameraVideo"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6'
                      }}
                      autoPlay
                      playsInline
                      muted
                    />
                    <div className="mt-3 d-flex justify-content-center gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={capturePhoto}
                        disabled={uploading}
                      >
                        <FontAwesomeIcon icon={faCamera} className="me-2" />
                        Take Photo & Upload
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={stopCameraPreview}
                        disabled={uploading}
                      >
                        Cancel
                      </Button>
                    </div>
                    {availableCameras.length > 1 && (
                      <div className="mt-2 text-muted small">
                        Using: {availableCameras.find(cam => cam.deviceId === currentCameraId)?.label || 'Default Camera'}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* File Selection Options */}
          {!cameraMode && (
            <div className="mb-4">
              <h6 className="mb-3">Choose an option:</h6>
              <ButtonGroup className="w-100 mb-3">
                <Button
                  variant="outline-primary"
                  onClick={startCameraPreview}
                  disabled={uploading}
                >
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Use Camera
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => document.getElementById('serviceItemFileInput')?.click()}
                  disabled={uploading}
                >
                  <FontAwesomeIcon icon={faUpload} className="me-2" />
                  Choose Files
                </Button>
              </ButtonGroup>
              
              <Form.Control
                id="serviceItemFileInput"
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          )}

                                           {/* Existing Images */}
            {selectedItemForImages?.imagesRequired && (
              <Alert
                variant={(selectedItemForImages.missingRequiredImageTypes?.length ?? 0) > 0 ? 'warning' : 'success'}
                className="py-2 mb-3"
              >
                {(selectedItemForImages.missingRequiredImageTypes?.length ?? 0) > 0 ? (
                  <>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Missing required photos:{' '}
                    {(selectedItemForImages.missingRequiredImageTypes ?? []).join(', ')}
                  </>
                ) : (
                  <>Required photos are complete for this item.</>
                )}
              </Alert>
            )}
            {selectedItemForImages?.vehicleServiceItemImages && selectedItemForImages.vehicleServiceItemImages.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-3">Existing Images ({selectedItemForImages.vehicleServiceItemImages.length}):</h6>
                <div className="row g-2">
                  {selectedItemForImages.vehicleServiceItemImages.map((image, index) => (
                    <div key={index} className="col-6 col-md-4">
                      <div className="card">
                        <div className="card-body p-2 text-center">
                                                     <div 
                             className="cursor-pointer"
                             onClick={() => openImageZoom(selectedItemForImages.vehicleServiceItemImages ?? [], index)}
                             style={{ cursor: 'pointer' }}
                           >
                                                           <RetryImage
                                googleFileId={image.googleFileId}
                                alt={image.filename}
                                thumbnail
                                fluid
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  objectFit: 'contain',
                                  borderRadius: '4px',
                                  marginBottom: '8px',
                                  backgroundColor: '#f8f9fa'
                                }}
                               onError={(e) => {
                                 const target = e.target as HTMLImageElement;
                                 target.style.backgroundColor = '#f8f9fa';
                                 target.style.display = 'flex';
                                 target.style.alignItems = 'center';
                                 target.style.justifyContent = 'center';
                                 target.style.color = '#6c757d';
                                 target.alt = 'Image not available';
                                 console.error(`Failed to load image: ${image.googleFileId}`, target.src);
                               }}
                             />
                           </div>
                          <div className="text-truncate small">{image.filename}</div>
                          <div className="text-muted small">{image.contentType}</div>
                          {image.isCategoryAnalysisPending ? (
                            <Badge bg="warning" text="dark" className="mt-1">
                              <Spinner animation="border" size="sm" className="me-1" />
                              Analyzing…
                            </Badge>
                          ) : image.vehicleImageCategoryName ? (
                            <Badge bg="info" className="mt-1">
                              {image.vehicleImageCategoryName}
                            </Badge>
                          ) : null}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="mt-1 w-100"
                            onClick={() => openCategoryModal({
                              vehicleImageId: image.vehicleImageId,
                              filename: image.filename,
                              currentCategoryId: image.vehicleImageCategoryId
                            })}
                            title="Set Category"
                          >
                            <FontAwesomeIcon icon={faEdit} className="me-1" />
                            {image.vehicleImageCategoryName ? 'Change Category' : 'Set Category'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

           {/* Selected Files Preview */}
           {selectedFiles.length > 0 && (
             <div className="mb-4">
               <h6 className="mb-3">Selected Files ({selectedFiles.length}):</h6>
               <div className="row g-2">
                 {selectedFiles.map((file, index) => (
                   <div key={index} className="col-6 col-md-4">
                     <div className="card">
                       <div className="card-body p-2 text-center">
                         <div className="text-truncate small">{file.name}</div>
                         <div className="text-muted small">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                         <Button
                           variant="outline-danger"
                           size="sm"
                           className="mt-1"
                           onClick={() => {
                             const newFiles = selectedFiles.filter((_, i) => i !== index);
                             setSelectedFiles(newFiles);
                           }}
                         >
                           Remove
                         </Button>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetImageModal} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={uploadFiles}
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
                 </Modal.Footer>
       </Modal>
       
       {/* Image Zoom Carousel Modal */}
       <Modal show={showZoomModal} onHide={closeImageZoom} size="xl" centered>
         <Modal.Header closeButton>
           <Modal.Title>
             <FontAwesomeIcon icon={faImage} className="me-2" />
             {carouselImages[carouselIndex]?.filename
               ?? `Photo ${carouselIndex + 1} of ${carouselImages.length}`}
           </Modal.Title>
         </Modal.Header>
         <Modal.Body className="text-center p-0">
           {carouselImages.length > 0 && (
             <Carousel
               activeIndex={carouselIndex}
               onSelect={handleCarouselSelect}
               interval={null}
               indicators={carouselImages.length > 1}
               controls={carouselImages.length > 1}
             >
               {carouselImages.map((image) => (
                 <Carousel.Item key={image.googleFileId}>
                   <div className="d-flex flex-column align-items-center justify-content-center p-3">
                     <RetryImage
                       googleFileId={image.googleFileId}
                       alt={image.filename}
                       fluid
                       style={{
                         maxWidth: '100%',
                         maxHeight: '70vh',
                         width: 'auto',
                         height: 'auto',
                         objectFit: 'contain',
                         transform:
                           image.googleFileId === carouselImages[carouselIndex]?.googleFileId
                             ? `rotate(${zoomRotation.rotationDegrees}deg)`
                             : undefined,
                         transition: 'transform 0.2s ease'
                       }}
                     />
                     {image.isCategoryAnalysisPending ? (
                       <Badge bg="warning" text="dark" className="mt-2">
                         <Spinner animation="border" size="sm" className="me-1" />
                         Analyzing category…
                       </Badge>
                     ) : image.vehicleImageCategoryName ? (
                       <Badge bg="secondary" className="mt-2">
                         {image.vehicleImageCategoryName}
                       </Badge>
                     ) : null}
                   </div>
                 </Carousel.Item>
               ))}
             </Carousel>
           )}
         </Modal.Body>
         <Modal.Footer className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
           <ImageRotateControls
             googleFileId={carouselImages[carouselIndex]?.googleFileId}
             rotationDegrees={zoomRotation.rotationDegrees}
             saving={zoomRotation.saving}
             onRotate={zoomRotation.rotateBy}
             onSave={() => void zoomRotation.saveRotation()}
           />
           {canEditService && vehicleService.status !== 'Submitted' && carouselImages[carouselIndex] ? (
             <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
               <Form.Label className="mb-0 small text-nowrap">Category</Form.Label>
               <Form.Select
                 size="sm"
                 style={{ maxWidth: 220 }}
                 value={selectedCategoryId ?? ''}
                 onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value, 10) : null)}
                 disabled={updatingCategory}
               >
                 <option value="">No Category</option>
                 {vehicleImageCategories.map(category => (
                   <option key={category.vehicleImageCategoryId} value={category.vehicleImageCategoryId}>
                     {category.vehicleImageCategoryName}
                   </option>
                 ))}
               </Form.Select>
               <Button
                 variant="primary"
                 size="sm"
                 onClick={() => void handleSetImageCategory(carouselImages[carouselIndex].vehicleImageId)}
                 disabled={updatingCategory}
               >
                 {updatingCategory ? (
                   <>
                     <Spinner as="span" animation="border" size="sm" className="me-2" />
                     Saving...
                   </>
                 ) : (
                   <>
                     <FontAwesomeIcon icon={faEdit} className="me-1" />
                     Save category
                   </>
                 )}
               </Button>
             </div>
           ) : (
             <div />
           )}
           <Button variant="secondary" onClick={closeImageZoom}>
             Close
           </Button>
         </Modal.Footer>
       </Modal>

       {/* Submit with missing photos warning */}
       <Modal show={showSubmitWarningModal} onHide={() => !submittingService && setShowSubmitWarningModal(false)} centered>
         <Modal.Header closeButton={!submittingService}>
           <Modal.Title>
             <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
             Missing required photos
           </Modal.Title>
         </Modal.Header>
         <Modal.Body>
           <p>
             Some product items are missing required photos.
             You can still submit; admins will be notified.
           </p>
           <ul className="mb-0">
             {itemsMissingRequiredImages.map(item => (
               <li key={item.vehicleServiceItemId}>
                 <strong>{item.productName || item.noIdProductName}</strong>
                 {': '}
                 {(item.missingRequiredImageTypes ?? []).join(', ')}
               </li>
             ))}
           </ul>
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={() => setShowSubmitWarningModal(false)} disabled={submittingService}>
             Cancel
           </Button>
           <Button variant="warning" onClick={() => void submitService()} disabled={submittingService}>
             {submittingService ? (
               <>
                 <Spinner as="span" animation="border" size="sm" className="me-2" />
                 Submitting...
               </>
             ) : (
               'Submit anyway'
             )}
           </Button>
         </Modal.Footer>
       </Modal>

       <Modal show={showMileageConfirmModal} onHide={() => !submittingService && setShowMileageConfirmModal(false)} centered>
         <Modal.Header closeButton={!submittingService}>
           <Modal.Title>
             <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
             Mileage is empty
           </Modal.Title>
         </Modal.Header>
         <Modal.Body>
           Mileage is empty or zero. Submit this service anyway?
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={() => setShowMileageConfirmModal(false)} disabled={submittingService}>
             Cancel
           </Button>
           <Button variant="warning" onClick={continueSubmitAfterMileageCheck} disabled={submittingService}>
             Continue
           </Button>
         </Modal.Footer>
       </Modal>

       <Modal show={showSubmitConfirmModal} onHide={() => !submittingService && setShowSubmitConfirmModal(false)} centered>
         <Modal.Header closeButton={!submittingService}>
           <Modal.Title>Confirm service</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           Submit this service? This will process all items and subtract quantities from inventory.
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={() => setShowSubmitConfirmModal(false)} disabled={submittingService}>
             Cancel
           </Button>
           <Button variant="success" onClick={() => void submitService()} disabled={submittingService}>
             {submittingService ? (
               <>
                 <Spinner as="span" animation="border" size="sm" className="me-2" />
                 Submitting...
               </>
             ) : (
               'Confirm Service'
             )}
           </Button>
         </Modal.Footer>
       </Modal>

       <Modal show={showRemoveItemModal} onHide={closeRemoveItemModal} centered>
         <Modal.Header closeButton={!removingItem}>
           <Modal.Title>Remove item</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           Remove this item from the service? It will remain visible with a strikethrough and will not affect inventory.
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={closeRemoveItemModal} disabled={removingItem}>
             Cancel
           </Button>
           <Button variant="danger" onClick={() => void confirmRemoveItem()} disabled={removingItem}>
             {removingItem ? (
               <>
                 <Spinner as="span" animation="border" size="sm" className="me-2" />
                 Removing...
               </>
             ) : (
               'Remove'
             )}
           </Button>
         </Modal.Footer>
       </Modal>
       
       {/* Set Image Category Modal */}
       <Modal show={showCategoryModal} onHide={closeCategoryModal} size="lg" centered>
         <Modal.Header closeButton>
           <Modal.Title>
             <FontAwesomeIcon icon={faEdit} className="me-2" />
             Set Image Category
           </Modal.Title>
         </Modal.Header>
         <Modal.Body>
           <div className="mb-3">
             <strong>Image:</strong> {selectedImageForCategory?.filename}
           </div>
           <Form.Group>
             <Form.Label>Category</Form.Label>
             <Form.Select
               value={selectedCategoryId || ''}
               onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : null)}
               disabled={updatingCategory}
             >
               <option value="">No Category</option>
               {vehicleImageCategories.map(category => (
                 <option key={category.vehicleImageCategoryId} value={category.vehicleImageCategoryId}>
                   {category.vehicleImageCategoryName}
                 </option>
               ))}
             </Form.Select>
           </Form.Group>
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={closeCategoryModal} disabled={updatingCategory}>
             Cancel
           </Button>
           <Button
             variant="primary"
             onClick={() => void handleSetImageCategory()}
             disabled={updatingCategory}
           >
             {updatingCategory ? (
               <>
                 <Spinner
                   as="span"
                   animation="border"
                   size="sm"
                   role="status"
                   aria-hidden="true"
                   className="me-2"
                 />
                 Updating...
               </>
             ) : (
               'Update Category'
             )}
           </Button>
         </Modal.Footer>
       </Modal>

      <HtmlPdfPreviewModal
        show={showInvoicePreview}
        onHide={closeInvoicePreview}
        title="PDF"
        preview={invoicePreview}
        isLoading={downloadingInvoice}
      />
     </div>
   );
 };

export default VehicleServiceDetail; 